import {
  antibioticL2Targets,
  AssayInfo,
  GeneGroupsByGrouping,
  GetGroup,
  l1TargetByL2,
  L1Targets,
  L2Targets,
  nonSixteenSL2Targets,
  sixteenS,
} from '@resistapp/common/assays';

import _, { Dictionary, get, keyBy, keys, values } from 'lodash';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useGet } from '../hooks/api';

interface AssayContextData {
  // All data and getters are initially unstable, but can be counted to be stable and not to cause re-renders after assays have loaded.
  allAssays: AssayInfo[];
  allGeneGroups: GeneGroupsByGrouping; // TODO rename
  getGroup: GetGroup;
  assaysLoaded: boolean;
  getAssayInfo: (assay: string) => AssayInfo | undefined;
}

const AssayContext = createContext<AssayContextData | undefined>(undefined);

export function AssayProvider({ children }: { children: ReactNode }) {
  const { data: assayInfoByAssay, isLoading } = useGet<Dictionary<AssayInfo>>('/assays');
  const [allAssays, setAllAssays] = useState<AssayInfo[]>([]);
  const [assayInfoByGene, setAssayInfoByGene] = useState<Dictionary<AssayInfo>>({});
  const [carbapenemGroups, setCarbapenemGroups] = useState<string[]>([]);
  const [mgeGroups, setMgeGroups] = useState<string[]>([]);

  const getGroupCacheRef = useRef<Record<string, string | undefined>>({});

  useEffect(() => {
    if (assayInfoByAssay) {
      const assays = values(assayInfoByAssay);
      setAllAssays(assays);
      setAssayInfoByGene(keyBy(assays, row => row.gene));
      setCarbapenemGroups(
        _.chain(assays)
          .map(row => row.carbapenem)
          .compact()
          .uniq()
          .sort()
          .value(),
      );
      setMgeGroups(
        _.chain(assays)
          .map(row => row.mge)
          .compact()
          .uniq()
          .sort()
          .value(),
      );
    }
  }, [assayInfoByAssay]);

  const allGeneGroups: GeneGroupsByGrouping = useMemo(() => {
    return {
      [sixteenS]: [sixteenS],
      l2Target: nonSixteenSL2Targets,
      l1Target: values(L1Targets),
      antibiotic: antibioticL2Targets,
      carbapenem: carbapenemGroups,
      mge: mgeGroups,
      assay: allAssays.map(assay => assay.assay),
    };
  }, [carbapenemGroups, mgeGroups, allAssays]);

  // This is not fully stable, as it's initially created without data, and causes a re-render once assays have loaded.
  // Thus, it is needed in dependency arrays, but can be relied on to be stable after assays have loaded.
  // NOTE! KEEP CONSISTENT WITH server/common.ts getGroupSync
  const getGroup = useCallback(
    (
      geneOrAssayOrTarget: string,
      grouping: 'l2Target' | 'l1Target' | 'carbapenem' | 'mge' | 'SIXTEENS_RRNA' | 'antibiotic' | 'assay' = 'l2Target',
    ) => {
      const cacheKey = `${geneOrAssayOrTarget}:${grouping}`;
      if (cacheKey in getGroupCacheRef.current) {
        return getGroupCacheRef.current[cacheKey];
      }

      if (geneOrAssayOrTarget in L2Targets) {
        let result: string | undefined = undefined;

        if (grouping === 'l2Target') {
          result = geneOrAssayOrTarget;
        } else if (grouping === 'l1Target') {
          result = l1TargetByL2[geneOrAssayOrTarget as L2Targets];
        } else if (grouping === 'antibiotic') {
          result = l1TargetByL2[geneOrAssayOrTarget as L2Targets] === L1Targets.ARG ? geneOrAssayOrTarget : undefined;
        }

        getGroupCacheRef.current[cacheKey] = result;
        return result;
      }

      if (isLoading) {
        // Odds are that some ignorant codepaths is calling before load has finished, and will be retriggered after.
        // Don't throw as that could result in timing dependent crashes, but do investigate and fix every occurance of this error!
        console.error(new Error(`getGroup called before assays have loaded (${geneOrAssayOrTarget} ${grouping})`));
        return undefined;
      }

      if (!keys(assayInfoByAssay).length) {
        throw Error(`Empty assays`);
      }

      const assayInfo =
        get(assayInfoByAssay, geneOrAssayOrTarget, undefined) || get(assayInfoByGene, geneOrAssayOrTarget, undefined);
      if (!assayInfo) {
        throw Error(`Unrecognized gene or assay in getGroup  ${geneOrAssayOrTarget}`);
      }

      // For 'assay' grouping, return the assay string itself
      if (grouping === 'assay') {
        return assayInfo.assay;
      }

      const group = assayInfo[grouping];
      if (grouping === 'l2Target' && !group) {
        throw Error('Assay info is missing target');
      }

      getGroupCacheRef.current[cacheKey] = group;
      return group;
    },
    [isLoading, assayInfoByAssay, assayInfoByGene],
  );

  const getAssayInfo = useCallback(
    (assay: string) => {
      if (isLoading || !assayInfoByAssay) {
        return undefined;
      }

      return assayInfoByAssay[assay];
    },
    [assayInfoByAssay, isLoading],
  );

  // If the data changes, we need to reset the cache
  useEffect(() => {
    getGroupCacheRef.current = {};
  }, [assayInfoByAssay, assayInfoByGene]);

  const assaysLoaded = !isLoading && allAssays.length > 0 && !!assayInfoByAssay && keys(assayInfoByAssay).length > 0;
  const contextValue: AssayContextData = {
    allGeneGroups,
    allAssays,
    getGroup,
    assaysLoaded,
    getAssayInfo,
  };

  return <AssayContext.Provider value={contextValue}>{children}</AssayContext.Provider>;
}

export function useAssayContext() {
  const context = useContext(AssayContext);
  if (!context) {
    throw new Error('useAssayContext must be used within an AssayProvider');
  }
  return context;
}
